import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Modal } from '@/components/ui/modal';
import { Select } from '@/components/ui/select';
import { FIELD_MAX_LENGTHS, DEV_EUI_PATTERN } from '@/lib/validation-constants';
import { DeviceType } from '@/types/api';
import type { Device, CreateDeviceRequest, Unit } from '@/types/api';

const DEVICE_TYPE_OPTIONS = [
  { value: 'WaterMeter', label: 'Water Meter' },
  { value: 'SALTTransmitter', label: 'SALT Transmitter' },
];

export function DeviceModal({
  device,
  units,
  onClose,
  onSave,
}: {
  device: Device | null;
  units: Unit[];
  onClose: () => void;
  onSave: (data: CreateDeviceRequest) => Promise<void>;
}) {
  // May 14 batch: Model + Firmware no longer surface in the create-path
  // UI. Wire fields still exist on CreateDeviceRequest for backwards
  // compatibility; we just stop sending them from the device modal.
  const [formData, setFormData] = useState<CreateDeviceRequest>({
    unitId: device?.unitId,
    devEui: device?.devEui || '',
    deviceType: device?.deviceType || DeviceType.WaterMeter,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const unitOptions = [
    { value: '', label: 'Unassigned' },
    ...units.map((u) => ({
      value: u.id,
      label: `${u.propertyName} - ${u.unitNumber}`,
    })),
  ];

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSave(formData);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={device ? 'Edit Device' : 'Provision New Device'}
      description="Enter the device details below"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="DevEUI"
          required
          value={formData.devEui}
          onChange={(e) =>
            setFormData({ ...formData, devEui: e.target.value.toUpperCase() })
          }
          placeholder="e.g., 0123456789ABCDEF"
          maxLength={FIELD_MAX_LENGTHS.DEV_EUI}
          pattern={DEV_EUI_PATTERN}
          helperText="16-character hexadecimal identifier"
          disabled={!!device}
        />

        <Select
          label="Device Type"
          required
          value={formData.deviceType}
          onChange={(val) =>
            setFormData({ ...formData, deviceType: val as DeviceType })
          }
          options={DEVICE_TYPE_OPTIONS}
        />

        <Select
          label="Assigned Unit"
          value={formData.unitId || ''}
          onChange={(val) =>
            setFormData({ ...formData, unitId: val || undefined })
          }
          options={unitOptions}
          searchable
        />

        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting} className="flex-1">
            {device ? 'Update' : 'Provision'} Device
          </Button>
        </div>
      </form>
    </Modal>
  );
}
